<template>
	<div v-if="(isUserAllowedInLevel5or6 && isAuthenticatedUserInGroupAdmin) || isUserAllowedInLevel1">
		<BaseHeader :title="$t('order_types').toString()" />
		<div v-if="!loading" class="mx-4">
			<div class="flex p-4">
				<p class="text-left">
					{{ $t('define_order_doc_types') }}
				</p>
			</div>
			<div class="flex p-4">
				<BaseInput v-model="newType.name" :label="$t('new_work_step')" class="flex-grow mr-4" @keyup.enter="add" />
				<div class="mt-8">
					<BaseButton @click="add" :disabled="!newType.name.length">{{ $t('add') }}</BaseButton>
				</div>
			</div>
			<div class="grid lg:gap-32 text-left mx-4" :class="{ 'md:grid-cols-2': !isUserAllowedInLevel1 }">
				<div>
					<div class="font-bold text-lg">{{ $t('global_order_doc_type') }}</div>
					<div class="mt-4 grid xxl:grid-cols-2 gap-x-12">
						<RepairPlanTaskRow
							v-for="(task, index) in sortedGlobalTypes"
							:key="task.id"
							:task="task"
							:index="index"
							@delete="deleteRow(task)"
							@change="registerChange(false)"
							:disabled="!isUserAllowedInLevel1"
						/>
					</div>
				</div>

				<div v-if="!isUserAllowedInLevel1">
					<div class="font-bold text-lg">{{ $t('company_wide_order_doc_types') }}</div>
					<div class="mt-4 grid xxl:grid-cols-2 gap-x-12">
						<RepairPlanTaskRow
							v-for="(task, index) in sortedTypes"
							:key="task.id"
							:task="task"
							:index="index"
							@change="registerChange(true)"
							@delete="deleteRow(task)"
							class="mb-2"
						/>
					</div>
				</div>
			</div>
		</div>
		<LoadingSpinner v-else />
		<Footer class="mx-8 justify-center" :is-back-button="false">
			<template #continueButton>
				<SaveButton @click="save" :disabled="disableButton" class="w-16 btn-blue">
					{{ $t('general.save') }}
				</SaveButton>
			</template>
		</Footer>
	</div>
	<Forbidden v-else />
</template>

<script>
import Validate from '@/mixins/Validate';
import { RepairPlanTaskApiFactory, RepairshopsApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import BaseHeader from '@/components/navigation/BaseHeader.vue';
import Footer from '@/components/damagecases/Footer';
import SaveButton from '@/components/forms/buttons/SaveButton.vue';
import Authentication from '@/mixins/Authentication';
import Forbidden from '@/views/default/Forbidden.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import OrderDocumentationTypesRepository from '@/api/repositories/OrderDocumentationTypesRepository';
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import RepairPlanTaskRow from '@/components/rows/RepairPlanTaskRow.vue';
import BaseInput from '@/components/forms/inputs/BaseInput.vue';
import { emptyCatch, makeShadowCopy, makeShadowCopyFromArrayElements } from '@/plugins/Utils';
import { Order } from '@stripe/stripe-js';

export default {
	name: 'OrderDocumentationTypes',
	components: { BaseInput, RepairPlanTaskRow, BaseButton, LoadingSpinner, Forbidden, SaveButton, BaseHeader, Footer },
	mixins: [Validate, Authentication],
	data() {
		return {
			newType: { name: '', id: 0 },
			newTypes: [],
			tasks: [],
			deleteList: [],
			originalTypes: [],
			originalGlobalTypes: [],
			sortedTypes: [],
			sortedGlobalTypes: [],
			timer: null,
			doRefresh: false,
			loading: false,
			disableButton: false,
			repository: new OrderDocumentationTypesRepository(),
		};
	},
	mounted() {
		this.fetchTypes();
	},
	computed: {
		// sortedTypes(){
		//   return [...this.originalTypes, ...this.newTypes].sort((a, b) => a.name.localeCompare(b.name));
		// },
		// sortedGlobalTypes(){
		//   return [...this.originalGlobalTypes, ...this.newTypes].sort((a, b) => a.name.localeCompare(b.name));
		// }
	},
	methods: {
		registerChange(isCompany) {
			clearTimeout(this.timer);
			this.timer = setTimeout(() => {
				if (isCompany) {
					if (this.originalTypes.length > 0) {
						this.sortedTypes = [...this.originalTypes, ...this.newTypes].sort((a, b) => a.name.localeCompare(b.name));
					} else {
						this.sortedTypes = [...this.newTypes];
					}
				} else {
					if (this.originalGlobalTypes.length > 0) {
						this.sortedGlobalTypes = [...this.originalGlobalTypes, ...this.newTypes].sort((a, b) => a.name.localeCompare(b.name));
					} else {
						this.sortedGlobalTypes = [...this.newTypes];
					}
				}
			}, 3000);
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
		},
		fetchTypes() {
			this.loading = true;
			this.repository
				.fetchAllOrderDocumentationTypes()
				.then(response => {
					this.loading = false;
					// this.tasks = response.data.data.map(makeShadowCopy);
					if (this.isUserAllowedInLevel1) {
						//this.tasks = response.data.data.map(makeShadowCopy);
						this.originalGlobalTypes = makeShadowCopyFromArrayElements(response.data.ownTypes);
						this.sortedGlobalTypes = response.data.ownTypes;
					} else {
						this.originalGlobalTypes = makeShadowCopyFromArrayElements(response.data.adminTypes);
						this.originalTypes = makeShadowCopyFromArrayElements(response.data.ownTypes);
						this.sortedGlobalTypes = response.data.adminTypes;
						this.sortedTypes = response.data.ownTypes;
					}
				})
				.catch(e => {
					this.loading = false;
					console.log(e);
				});
		},
		async save() {
			this.loading = true;
			const promise = [];
			this.newTypes.forEach(type => {
				promise.push(() => this.repository.storeOrderDocumentationType({ name: type.name }));
			});
			this.deleteList.forEach(type => {
				promise.push(() => this.repository.deleteOrderDocumentationType(type.id));
			});

			const typeList = this.isUserAllowedInLevel1 ? this.originalGlobalTypes : this.originalTypes;

			for (let i = 0; i < typeList.length; i++) {
				const row = typeList[i];
				const isChanged = row._shadow.name !== row.name;
				if (isChanged) {
					promise.push(() => this.repository.updateOrderDocumentationType(row.id, row));
				}
			}

			Promise.all(promise.map(fn => fn()))
				.then(response => {
					this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
					this.$store.commit('setConfirmLeaving', false);
					this.loading = false;
					this.fetchTypes();
					this.deleteList = [];
					this.newTypes = [];
					this.newType = { name: '', id: 0 };
				})
				.catch(error => {
					console.log(error);
					this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
					this.loading = false;
				});
		},
		deleteRow(task) {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			if (task.isNew) {
				const index = this.newTypes.findIndex(e => e.id === task.id);
				this.newTypes.splice(index, 1);
			} else {
				this.deleteList.push(task);
				if (this.isUserAllowedInLevel1) {
					const index = this.originalGlobalTypes.findIndex(e => e.id === task.id);
					this.originalGlobalTypes.splice(index, 1);
				} else {
					const index = this.originalTypes.findIndex(e => e.id === task.id);
					this.originalTypes.splice(index, 1);
				}
			}
			if (this.isUserAllowedInLevel1) {
				if (this.originalGlobalTypes.length > 0) {
					this.sortedGlobalTypes = [...this.originalGlobalTypes, ...this.newTypes].sort((a, b) => a.name.localeCompare(b.name));
				} else {
					this.sortedGlobalTypes = [...this.newTypes];
				}
			} else {
				if (this.originalTypes.length > 0) {
					this.sortedTypes = [...this.originalTypes, ...this.newTypes].sort((a, b) => a.name.localeCompare(b.name));
				} else {
					this.sortedTypes = [...this.newTypes];
				}
			}
		},
		findIndex(array) {
			let largest = 0;

			for (let i = 0; i < array.length; i++) {
				if (array[i].id > largest) {
					largest = array[i].id;
				}
			}
			return largest + 1;
		},
		add() {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			if (this.newTypes.length === 0) {
				if (this.isUserAllowedInLevel1) {
					this.newType.id = this.findIndex(this.sortedGlobalTypes);
				} else {
					this.newType.id = this.findIndex(this.sortedTypes);
				}
			} else {
				this.newType.id = this.newType.id + 1;
			}
			this.newTypes.push({ name: this.newType.name, isNew: true, id: this.newType.id });
			this.newType.name = '';
			if (this.isUserAllowedInLevel1) {
				if (this.originalGlobalTypes.length > 0) {
					this.sortedGlobalTypes = [...this.originalGlobalTypes, ...this.newTypes].sort((a, b) => a.name.localeCompare(b.name));
				} else {
					this.sortedGlobalTypes = [...this.newTypes];
				}
			} else {
				if (this.originalTypes.length > 0) {
					this.sortedTypes = [...this.originalTypes, ...this.newTypes].sort((a, b) => a.name.localeCompare(b.name));
				} else {
					this.sortedTypes = [...this.newTypes];
				}
			}
		},
	},
};
</script>
